import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { MobileShell } from "@/components/MobileShell";
import { Spinner } from "@/components/kit";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BasicProfileStep from "./steps/BasicProfileStep";
import PhotosPromptsStep from "./steps/PhotosPromptsStep";
import PreferencesStep from "./steps/PreferencesStep";
import ReviewStep from "./steps/ReviewStep";

const STEPS = ["About you", "Photos & prompts", "Who you'd like to meet", "Review & publish"];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const editMode = params.get("edit") === "1";
  const [step, setStep] = useState(0);
  const [reference, setReference] = useState(null);

  useEffect(() => {
    api.get("/reference").then(({ data }) => setReference(data)).catch(() => {});
  }, []);

  // If already published and not in edit mode, send to app.
  useEffect(() => {
    if (editMode) return;
    api.get("/profile/completion").then(({ data }) => {
      if (data.published) navigate("/app/discover", { replace: true });
    }).catch(() => {});
  }, [editMode, navigate]);

  const next = useCallback(() => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
  }, [step]);
  const back = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
    else navigate(editMode ? "/app/profile" : "/welcome");
  }, [step, editMode, navigate]);

  const finishEdit = () => { toast.success("Profile updated."); navigate("/app/profile"); };

  if (!reference) {
    return <MobileShell><div className="min-h-screen flex items-center justify-center"><Spinner /></div></MobileShell>;
  }

  const stepProps = { reference, onNext: next, onBack: back, editMode, user };

  return (
    <MobileShell>
      <header className="sticky top-0 z-40 bg-[#F8F6F1]/90 backdrop-blur-md border-b border-[#E3DCD0] px-5 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <button onClick={back} className="text-[#5F6D66] -ml-2 p-2" data-testid="onboarding-back-btn">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="font-sans text-xs tracking-wide text-[#8A9790]">Step {step + 1} of {STEPS.length}</span>
          <div className="w-9" />
        </div>
        <div className="mt-3 flex gap-1.5" data-testid="onboarding-progress">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#253B35]" : "bg-[#E3DCD0]"}`} />
          ))}
        </div>
        <h2 className="font-serif text-2xl text-[#1F2B26] mt-4">{STEPS[step]}</h2>
      </header>

      <div className="px-6 py-6 pb-28">
        {step === 0 && <BasicProfileStep {...stepProps} />}
        {step === 1 && <PhotosPromptsStep {...stepProps} />}
        {step === 2 && <PreferencesStep {...stepProps} />}
        {step === 3 && <ReviewStep {...stepProps} onFinishEdit={finishEdit} />}
      </div>
    </MobileShell>
  );
}
